/* eslint-disable @typescript-eslint/no-explicit-any */
// "Daily check-in" — today's mood (URS_12.2 / ACDPA-224). Nothing else.
//
// ⛔ F-538 (Leah, UAT — coordinator ruling 1): THE 30/60/90-DAY SURVEYS ARE NOT ON THIS SCREEN.
// *"This screen is only intended for daily check-in."* Surveys appear ONLY under Settings > CARE
// TEAM > Surveys (`CareTeamSurveys.tsx`). This screen used to list them too, and its
// `listMySurveys()` call was one of the reads that ISSUES a due survey (E12d/R2 — the kernel has no
// scheduler). Issuing is not lost by removing it: the kernel issues on `GET /me/engagement/surveys`
// (Care Team > Surveys) AND on `POST /me/engagement/sync`, which the Settings screen — the only way
// into this screen — and the Care Team inbox call on every visit (`useEngagementSync`).
//
// ⛔ THE MOOD PICKER'S OPTIONS COME FROM THE SERVER, not from a constant in this app. The app
// already has its own `MOODS` list for journal posts (`src/types/socialJournal.ts`), and that
// list is blaze's vocabulary, not the kernel's `mood_value_lkup`. Using it here would send
// codes the kernel refuses with `MOOD_VALUE_UNKNOWN`. The five codes below are read from the
// patient's own history when there is any, and otherwise fall back to the seeded five — which
// is stated, not hidden, because it is the one place this file guesses.
//
// ⛔ ONE CHECK-IN PER DAY, enforced by the kernel (409 `MOOD_CHECKIN_ALREADY_RECORDED`). This
// screen reads today's history first so it can say "already recorded" instead of offering a
// button whose only outcome is an error.
//
// ⭐ F-483 (Leah, UAT3) — TODAY'S CHECK-IN CAN BE EDITED, IN PLACE. The confirmation shows the
// feeling, then "Recorded today." on its own line, then the patient's own note; an Edit button
// reopens the picker pre-filled and saves with `PATCH` (`updateMoodCheckin`). Only today's row
// is ever offered for editing — `todaysCheckin` is keyed on the server's `today` — and the kernel
// refuses any other day (`MOOD_CHECKIN_EDIT_WINDOW_CLOSED`), so it is still one check-in a day.

import React, { useCallback, useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonButton,
  IonTextarea,
  IonToast,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonNote,
  IonIcon,
  RefresherEventDetail,
  useIonRouter,
} from "@ionic/react";
import { checkmarkCircleOutline } from "ionicons/icons";
import Header from "../../components/Header";
import { useHaptics } from "../../hooks/useHaptics";
import { useEngagementFeatures } from "../../hooks/useEngagementFeatures";
import {
  MoodCheckin,
  engagementErrorMessage,
  listMyMoodCheckins,
  recordMoodCheckin,
  updateMoodCheckin,
} from "../../services/engagement";
import "./Engagement.css";

/**
 * ⚠️ THE FALLBACK, AND ONLY THE FALLBACK. Seeded `mood_value_lkup` codes with the emoji the
 * kernel serves alongside them. Used only until the patient has one check-in of their own, at
 * which point the labels come back from the server with the row. If this list ever disagrees
 * with the kernel the symptom is a 422 `MOOD_VALUE_UNKNOWN` on submit, which the toast shows.
 */
const FALLBACK_MOODS = [
  { code: "very_sad", label: "Very low", emoji: "😞" },
  { code: "sad", label: "Low", emoji: "🙁" },
  { code: "neutral", label: "Neutral", emoji: "😐" },
  { code: "happy", label: "Good", emoji: "🙂" },
  { code: "very_happy", label: "Very good", emoji: "😄" },
];

/**
 * ⛔ THERE IS NO `localToday()` HERE ANY MORE, AND ITS ABSENCE IS THE DELIVERABLE (E12d/R3).
 * The app used to compute the day from the device clock and send it with every check-in,
 * because the kernel defaulted to UTC. Two clocks decided one clinical fact. The kernel now
 * resolves the patient's own local day from `profile.time_zone` and reports it as `today` on
 * `GET /me/engagement/mood-checkins`; this screen reads that and computes no date at all, so
 * "have I checked in today?" and `MOOD_CHECKIN_ALREADY_RECORDED` can never disagree.
 */

const CheckIn: React.FC = () => {
  const router = useIonRouter();
  const { successActivity, errorFeedback, selectionFeedback } = useHaptics();
  const { features, isLoading: featuresLoading, hasOrganization } = useEngagementFeatures();

  // ⚠️ `null` until the server says. The screen shows its skeleton rather than guessing a
  // date, because a guessed date is exactly what R3 removed.
  const [today, setToday] = useState<string | null>(null);
  const [todaysCheckin, setTodaysCheckin] = useState<MoodCheckin | null>(null);
  const [history, setHistory] = useState<MoodCheckin[]>([]);
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // F-483 — true while the patient is changing today's already-recorded check-in.
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const moods = await listMyMoodCheckins();
      setHistory(moods.items);
      setToday(moods.today);
      setTodaysCheckin(
        moods.items.find(
          (row) => row.checkin_date === moods.today && row.source.code === "mobile_app",
        ) ?? null,
      );
    } catch (err) {
      setError(engagementErrorMessage(err, "Your check-ins could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await load();
    event.detail.complete();
  };

  // Prefer the server's own vocabulary once anything has come back carrying it.
  const moodOptions = (() => {
    const seen = new Map<string, { code: string; label: string; emoji: string | null }>();
    history
      .slice()
      .sort((a, b) => a.mood.score - b.mood.score)
      .forEach((row) =>
        seen.set(row.mood.code, {
          code: row.mood.code,
          label: row.mood.label,
          emoji: row.mood.emoji,
        }),
      );
    // ⚠️ `Array.from`, not a spread: this project compiles to `target: es5` without
    // `downlevelIteration`, so spreading a Map iterator is a compile error rather than a
    // style choice.
    return seen.size >= FALLBACK_MOODS.length ? Array.from(seen.values()) : FALLBACK_MOODS;
  })();

  const startEdit = () => {
    if (!todaysCheckin) return;
    setSelectedMood(todaysCheckin.mood.code);
    setNote(todaysCheckin.note ?? "");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setSelectedMood(null);
    setNote("");
  };

  const save = async () => {
    if (!selectedMood || isSaving) return;
    setIsSaving(true);
    try {
      // ⛔ NO DATE. The server decides which day this is for this patient — see the header.
      const saved =
        isEditing && todaysCheckin
          ? // F-483: with the note box hidden (toggle off) the note is left alone, not cleared.
            await updateMoodCheckin(
              todaysCheckin.id,
              selectedMood,
              features.mood_response_enabled ? note : undefined,
            )
          : await recordMoodCheckin(selectedMood, features.mood_response_enabled ? note : null);
      const wasEditing = isEditing;
      setTodaysCheckin(saved);
      setIsEditing(false);
      setSelectedMood(null);
      setNote("");
      await successActivity();
      setMessage(
        wasEditing
          ? "Your check-in has been updated."
          : "Thanks — your check-in has been shared with your care team.",
      );
      await load();
    } catch (err) {
      await errorFeedback();
      setError(engagementErrorMessage(err, "Your check-in could not be saved."));
    } finally {
      setIsSaving(false);
    }
  };

  // ⚠️ `today === null` keeps the skeleton up until the server has said which day it is, so
  // the screen never renders "how are you feeling today?" against a date it invented.
  const busy = isLoading || featuresLoading;
  // ⛔ THE SCREEN WILL NOT INVENT A DATE. `today` is the server's answer about the
  // patient's own day (E12d/R3); if the read failed we do not have one, and offering a
  // "how are you feeling today?" form would be asking about a day nobody has named. The
  // toast already carries the server's (or the network's) own sentence.
  const dayUnknown = hasOrganization && !busy && today === null;

  return (
    <IonPage>
      {/* F-530 (Leah, UAT) — same header treatment as the Messages list (F-481) and the
          conversation screen (F-488): `Header` defaults `showGreeting`/`showUserIcon` to true,
          which put "Welcome," and the stacked-avatar row above "Daily check-in". Both off, and
          the same `engagement-messages-header` wrapper closes up the space they reserved. */}
      <div className="engagement-messages-header">
        <Header
          showGreeting={false}
          showUserIcon={false}
          showPageTitle
          pageTitle="Daily check-in"
          showBackButton
          onBackButtonClick={() => router.goBack()}
        />
      </div>
      <IonContent className="engagement-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {busy ? (
          <div className="engagement-card">
            <IonSkeletonText animated style={{ width: "50%", height: 20 }} />
            <IonSkeletonText animated style={{ height: 64 }} />
          </div>
        ) : !hasOrganization ? (
          <div className="engagement-empty">
            <IonIcon icon={checkmarkCircleOutline} />
            <h2>No care team yet</h2>
            <p>Check-ins are shared with the provider looking after your therapy.</p>
          </div>
        ) : dayUnknown ? (
          <div className="engagement-empty">
            <IonIcon icon={checkmarkCircleOutline} />
            <h2>Check-ins are unavailable</h2>
            <p>We could not reach your care team just now. Pull down to try again.</p>
          </div>
        ) : (
          <>
            <div className="engagement-card">
              <h2>How are you feeling today?</h2>
              {todaysCheckin && !isEditing ? (
                <>
                  <div className="engagement-done" data-testid="mood-confirmation">
                    <span className="engagement-done-emoji">
                      {todaysCheckin.mood.emoji ?? "✓"}
                    </span>
                    {/* F-483: the feeling and "Recorded today." are separate lines (they ran
                        together as "Very goodRecorded today."), and the note is shown back. */}
                    <div className="engagement-done-text">
                      <strong>{todaysCheckin.mood.label}</strong>{" "}
                      <IonNote className="engagement-done-line">
                        Recorded today. Your care team can see it on your record.
                      </IonNote>
                      {todaysCheckin.note && (
                        <p className="engagement-done-note" data-testid="mood-confirmation-note">
                          {todaysCheckin.note}
                        </p>
                      )}
                    </div>
                  </div>
                  <IonButton expand="block" fill="outline" onClick={startEdit}>
                    Edit check-in
                  </IonButton>
                </>
              ) : (
                <>
                  <div className="engagement-moods">
                    {moodOptions.map((mood) => (
                      <button
                        key={mood.code}
                        type="button"
                        className={`engagement-mood ${
                          selectedMood === mood.code ? "is-selected" : ""
                        }`}
                        onClick={() => {
                          void selectionFeedback();
                          setSelectedMood(mood.code);
                        }}
                      >
                        <span className="engagement-mood-emoji">{mood.emoji ?? "•"}</span>
                        <span className="engagement-mood-label">{mood.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* URS_12.5: the toggle governs the NOTE, not the check-in. When it is off the
                      box is not shown — the kernel would refuse a note rather than drop it, and
                      offering a box whose contents can only be rejected is worse than not
                      offering one. */}
                  {features.mood_response_enabled && (
                    <IonTextarea
                      label="Anything you want them to know? (optional)"
                      labelPlacement="stacked"
                      autoGrow
                      rows={3}
                      value={note}
                      onIonInput={(e) => setNote(String(e.detail.value ?? ""))}
                    />
                  )}

                  <IonButton expand="block" disabled={!selectedMood || isSaving} onClick={save}>
                    {isSaving ? "Saving…" : isEditing ? "Save changes" : "Save check-in"}
                  </IonButton>
                  {isEditing && (
                    <IonButton expand="block" fill="clear" disabled={isSaving} onClick={cancelEdit}>
                      Cancel
                    </IonButton>
                  )}
                </>
              )}
            </div>

            {history.length > 0 && (
              <div className="engagement-card">
                <h2>Recent check-ins</h2>
                <ul className="engagement-history">
                  {history.slice(0, 14).map((row) => (
                    <li key={row.id}>
                      <span>{row.mood.emoji ?? "•"}</span>
                      <span>{row.mood.label}</span>
                      <IonNote>{row.checkin_date}</IonNote>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <IonToast
          isOpen={!!error}
          message={error ?? ""}
          duration={5000}
          color="danger"
          onDidDismiss={() => setError(null)}
        />
        <IonToast
          isOpen={!!message}
          message={message ?? ""}
          duration={3000}
          color="success"
          onDidDismiss={() => setMessage(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default CheckIn;
